import {
  EventFeedTeaseMetaItemProps,
  EventFeedTeaseProps,
} from "design-system/components/primitives/event-feed-tease/event-feed-tease";
import { LegacySearchHit, SearchDisplay } from "frontend/types";
import { EventsTeaseItemProps } from "design-system/components/blocks/events-tease/events-tease-item";

export function createEventFeedTeaseProps(
  data: SearchDisplay,
): EventFeedTeaseProps {
  const props: EventFeedTeaseProps = {
    type: "Event",
    title: data.title,
    href: data.display?.eventLink,
    dates: [],
    overline: data.display?.overline?.label,
    meta: [],
    featuredImage: undefined,
    id: data.id,
  };

  const [start, end] = data.display?.dateRange?.map(String) ?? [];
  // Compare just the date portion to determine if it's a single day event or multi day event
  const startDate = start?.split("T")[0];
  const endDate = end?.split("T")[0];

  props.dates =
    start && end
      ? startDate === endDate
        ? [start]
        : [start, end]
      : start
        ? [start]
        : [];

  props.meta = data.display?.eventMeta;

  if (data.display?.thumbnail) {
    props.featuredImage = {
      src: data.display.thumbnail.src,
      alt: data.display.thumbnail.alt,
    };
  }

  if (data.description) {
    props.blurb = data.description;
  }

  return props;
}

export function createLegacyEventTeaseProps(
  data: LegacySearchHit,
): EventFeedTeaseProps {
  if (
    !data.HBSTitle ||
    (!data.HBSAlumniEventsStartDate && !data.HBSStartDate)
  ) {
    console.warn("Legacy Event Tease Props - missing HBSTitle or start date");
    return {} as EventFeedTeaseProps;
  }

  const props: EventFeedTeaseProps = {
    type: "Event",
    title: data.HBSTitle,
    href: data.HBSLink,
    dates: [],
    overline: data.HBSAlumniClubDesc || data.HBSEventTypeDesc,
    meta: [],
    featuredImage: undefined,
    id: data.id,
  };
  const start = data.HBSAlumniEventsStartDate || data.HBSStartDate;
  const end = data.HBSAlumniEventsEndDate;
  const eventTeaseArray: Array<EventFeedTeaseMetaItemProps> = [];

  if (start || end) {
    props.dates =
      start && end
        ? start.split("T")[0] === end.split("T")[0]
          ? [start]
          : [start, end]
        : start
          ? [start]
          : [];
  }

  if (
    data.HBSDisplayDate &&
    (data.HBSDisplayDate.indexOf(":") > -1 ||
      data.HBSDisplayDate.indexOf(" AM ") > -1 ||
      data.HBSDisplayDate.indexOf(" PM ") > -1)
  ) {
    eventTeaseArray.push({
      title: "Times",
      info: data.HBSDisplayDate,
    });
  }

  if (data.HBSLocation) {
    eventTeaseArray.push({
      title: "Location",
      info: data.HBSLocation,
    });
  }

  // Group every 2 meta items together for the tease, design supports 2 items per row
  for (let i = 0; i < eventTeaseArray.length; i += 2) {
    props.meta?.push(eventTeaseArray.slice(i, i + 2));
  }

  if (data.HBSThumbnail) {
    props.featuredImage = {
      src: data.HBSThumbnail,
    };
  }

  if (data.HBSDescription || data.HBSBody) {
    const blurb = data.HBSDescription || data.HBSBody;
    props.blurb = decodeBasicEntities(blurb);
  }

  return props;
}

function parseTimeRange(timeString: string): [string, string] | string {
  const parts = timeString.split(" - ");
  if (parts.length === 2) {
    // If there are two parts, it's a range
    return parts.map(parseTime) as [string, string];
  } else if (parts.length === 1) {
    // If there's only one part, it's a single time
    const parsedTime = parseTime(parts[0] ?? "");
    return parsedTime;
  }
  return timeString;
}

function parseTime(time: string): string {
  const date = new Date();
  const [hours, minutes] = time.split(":").map((part) => part.trim());
  const isPM = time.toLowerCase().includes("pm");
  if (hours && minutes) {
    const parsedHours = parseInt(hours);
    const adjustedHours =
      isPM && parsedHours !== 12 ? parsedHours + 12 : parsedHours;
    date.setHours(adjustedHours, parseInt(minutes));
    return date.toISOString();
  }
  return time; // Return original time if parsing fails
}

function getEventTime(
  eventMeta: Array<Array<{ title?: string; info?: string }>> | undefined,
): string | [string, string] | undefined {
  if (!eventMeta) return undefined;

  for (const metaGroup of eventMeta) {
    const timeObject = metaGroup.find(
      (item) => item.title?.toLowerCase() === "times",
    );
    if (timeObject?.info) {
      return parseTimeRange(timeObject.info);
    }
  }
  return undefined;
}

export function createEventTeaseProps(
  data: SearchDisplay,
): EventsTeaseItemProps {
  const props: EventsTeaseItemProps = {
    event: {
      title: data.title,
      location: data.location ?? "",
      date: "",
      href: data.display?.eventLink,
      featuredImage: data.display?.thumbnail
        ? {
            src: data.display.thumbnail.src,
            alt: data.display.thumbnail.alt,
          }
        : undefined,
    },
  };
  const [start, end] = data.display?.dateRange?.map(String) ?? [];
  props.event.date =
    start && end ? (start === end ? start : [start, end]) : start ? start : "";

  const eventTime = getEventTime(data.display?.eventMeta);
  props.event.time = eventTime;

  return props;
}
// Minimal SSR-safe decoder for common entities
export function decodeBasicEntities(text?: string): string | undefined {
  if (!text) return text;
  return text
    .replace(/&nbsp;/g, " ")
    .replace(/&mdash;/g, "—")
    .replace(/&ndash;/g, "–")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}
